import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { EMPLOYEE_NAV } from "./_nav";
import { api, errFrom, API, absUrl } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { MapPin, Play, Square, TrendingUp, Coffee, CheckCircle2, FileText, Download } from "lucide-react";

function useGeolocation() {
  return async () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
}

const BREAK_OPTIONS = [15, 30, 45, 60];

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [activeBreak, setActiveBreak] = useState(null);
  const [hours, setHours] = useState({ today_hours: 0, week_hours: 0, month_hours: 0, today_break_minutes: 0, recent: [] });
  const [policy, setPolicy] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [breakElapsed, setBreakElapsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [breakOpen, setBreakOpen] = useState(false);
  const getGeo = useGeolocation();

  async function load() {
    try {
      const [st, br, hr, sops] = await Promise.all([
        api.get("/employee/punch/status"),
        api.get("/employee/break/status"),
        api.get("/employee/hours"),
        api.get("/employee/sops"),
      ]);
      setActive(st.data.active);
      setActiveBreak(br.data.active_break);
      setHours(hr.data);
      setPolicy(sops.data.policy);
    } catch (e) { toast.error(errFrom(e)); }
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!active) { setElapsed(0); return; }
    const t = setInterval(() => {
      const start = new Date(active.clock_in_at).getTime();
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (!activeBreak) { setBreakElapsed(0); return; }
    const t = setInterval(() => {
      const start = new Date(activeBreak.start_at).getTime();
      setBreakElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [activeBreak]);

  async function doGeo() {
    try { return await getGeo(); }
    catch (e) { toast.error("Please enable location access"); throw e; }
  }

  async function punchIn() {
    setLoading(true);
    try {
      const { latitude, longitude } = await doGeo();
      await api.post("/employee/punch/in", { latitude, longitude });
      toast.success("Punched in");
      await load();
    } catch (e) { toast.error(errFrom(e) || e.message); }
    finally { setLoading(false); }
  }
  async function punchOut() {
    setLoading(true);
    try {
      const { latitude, longitude } = await doGeo();
      await api.post("/employee/punch/out", { latitude, longitude });
      toast.success("Punched out. Nice work.");
      await load();
    } catch (e) { toast.error(errFrom(e) || e.message); }
    finally { setLoading(false); }
  }
  async function startBreak(mins) {
    setLoading(true); setBreakOpen(false);
    try {
      const { latitude, longitude } = await doGeo();
      await api.post("/employee/break/start", { planned_minutes: mins, latitude, longitude });
      toast.success(`Break started — enjoy your ${mins} minutes`);
      await load();
    } catch (e) { toast.error(errFrom(e) || e.message); }
    finally { setLoading(false); }
  }
  async function endBreak() {
    setLoading(true);
    try {
      const { latitude, longitude } = await doGeo();
      const { data } = await api.post("/employee/break/end", { latitude, longitude });
      toast.success(`Break ended · ${data.actual_minutes}m`);
      await load();
    } catch (e) { toast.error(errFrom(e) || e.message); }
    finally { setLoading(false); }
  }

  async function downloadReceipt() {
    try {
      const r = await fetch(`${API}/employee/policy-receipt`, { credentials: "include" });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Unable to generate receipt"); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `policy-receipt-${(user?.name || "employee").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Download failed"); }
  }

  function fmt(sec) {
    const total = Math.max(0, Math.floor(sec || 0));
    const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  const overBreak = activeBreak && breakElapsed / 60 > (activeBreak.planned_minutes || 0);

  return (
    <AppShell nav={EMPLOYEE_NAV} title={`Hello, ${user?.name?.split(" ")[0] || "Team"}`} subtitle="Employee Dashboard">
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        {/* Punch clock */}
        <div className="lg:col-span-2 brand-surface rounded-2xl p-5 md:p-8 relative overflow-hidden" data-testid="punch-card">
          <div className={`absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl ${activeBreak ? "bg-amber-500/20" : active ? "bg-green-500/20" : "bg-[#FF3B30]/20"}`} />
          <div className="relative">
            <div className="text-overline mb-2">
              {activeBreak ? "On Break" : active ? "Clocked In" : "Ready to start"}
            </div>
            {activeBreak ? (
              <>
                <div className={`font-mono text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight ${overBreak ? "text-red-400" : "text-amber-400"}`} data-testid="break-elapsed">
                  {fmt(breakElapsed)}
                </div>
                <div className="mt-2 text-sm text-zinc-400">Planned: {activeBreak.planned_minutes} min {overBreak && <span className="text-red-400 font-semibold ml-2">· overtime (unpaid)</span>}</div>
                {activeBreak.start_address && (
                  <div className="mt-3 flex items-center gap-2 text-zinc-500 text-xs"><MapPin size={12} /><span className="truncate">Started at: {activeBreak.start_address}</span></div>
                )}
                <Button onClick={endBreak} disabled={loading}
                  className="mt-6 bg-white text-black hover:bg-zinc-200 font-semibold h-14 px-8 rounded-xl text-base active:scale-[0.98]"
                  data-testid="end-break-button">
                  <CheckCircle2 className="mr-2" size={20} /> End break
                </Button>
                <p className="mt-3 text-xs text-zinc-500">Location will be captured when you end the break.</p>
              </>
            ) : (
              <>
                <div className="font-mono text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight" data-testid="punch-elapsed">
                  {active ? fmt(elapsed) : "00:00:00"}
                </div>
                {active?.clock_in_address && (
                  <div className="mt-3 flex items-center gap-2 text-zinc-400 text-sm"><MapPin size={14} className="text-[#FF3B30]" /><span className="truncate">{active.clock_in_address}</span></div>
                )}
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  {!active ? (
                    <Button onClick={punchIn} disabled={loading}
                      className="bg-[#FF3B30] hover:bg-[#E02920] text-white font-semibold h-16 px-10 rounded-xl text-lg active:scale-[0.98]"
                      data-testid="punch-in-button">
                      <Play className="mr-3" size={22} fill="white" /> Punch In
                    </Button>
                  ) : (
                    <>
                      <Button onClick={punchOut} disabled={loading}
                        className="bg-white text-black hover:bg-zinc-200 font-semibold h-16 px-8 rounded-xl text-lg active:scale-[0.98]"
                        data-testid="punch-out-button">
                        <Square className="mr-3" size={18} fill="black" /> Punch Out
                      </Button>
                      <div className="relative">
                        <Button onClick={() => setBreakOpen(!breakOpen)} disabled={loading} variant="secondary"
                          className="bg-[#1C1C1E] border border-white/10 hover:bg-[#2C2C2E] text-white h-16 px-6 rounded-xl text-base"
                          data-testid="take-break-button">
                          <Coffee size={18} className="mr-2 text-amber-400" /> Take a break
                        </Button>
                        {breakOpen && (
                          <div className="absolute left-0 mt-2 z-10 brand-surface rounded-xl p-3 min-w-[220px] shadow-2xl" data-testid="break-options">
                            <div className="text-overline mb-2 px-1">Unpaid · select length</div>
                            <div className="grid grid-cols-2 gap-2">
                              {BREAK_OPTIONS.map((m) => (
                                <button key={m} onClick={() => startBreak(m)} className="brand-surface-2 rounded-lg py-3 font-semibold hover-lift text-sm" data-testid={`break-option-${m}`}>
                                  {m}<span className="text-zinc-500 text-xs ml-1">min</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <p className="mt-4 text-xs text-zinc-500">Location is captured on punch in/out &amp; at the start/end of each break.</p>
              </>
            )}
          </div>
        </div>

        {/* Hours stats */}
        <div className="grid grid-cols-3 lg:grid-cols-1 lg:flex lg:flex-col gap-3 lg:gap-4">
          <StatCard label="Today" value={`${hours.today_hours}h`} sub={hours.today_break_minutes > 0 ? `${hours.today_break_minutes}m break` : null} accent />
          <StatCard label="This Week" value={`${hours.week_hours}h`} sub={hours.week_break_minutes > 0 ? `${hours.week_break_minutes}m break` : null} />
          <StatCard label="This Month" value={`${hours.month_hours}h`} sub={hours.month_break_minutes > 0 ? `${hours.month_break_minutes}m break` : null} />
        </div>
      </div>

      {/* Documents */}
      <div className="mt-8 brand-surface rounded-2xl p-6" data-testid="documents-card">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Documents</h2>
          <div className="text-overline">Policy</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <a href={policy?.url ? absUrl(policy.url) : "#"} target="_blank" rel="noopener noreferrer" className="brand-surface-2 rounded-lg p-4 flex items-center gap-3 hover-lift" data-testid="view-policy-button">
            <div className="w-10 h-10 rounded-lg bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><FileText size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-sm">Onboarding Policy &amp; Procedure</div>
              <div className="text-[11px] text-zinc-500">View the official policy PDF</div>
            </div>
          </a>
          <button onClick={downloadReceipt} className="brand-surface-2 rounded-lg p-4 flex items-center gap-3 hover-lift text-left" data-testid="download-policy-receipt-button">
            <div className="w-10 h-10 rounded-lg bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><Download size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-sm">My Signed Policy Receipt</div>
              <div className="text-[11px] text-zinc-500">Download PDF receipt with your signature</div>
            </div>
          </button>
        </div>
      </div>

      {/* Recent */}
      <div className="mt-8 brand-surface rounded-2xl p-4 md:p-6" data-testid="recent-punches">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg md:text-xl font-bold">Recent punches</h2>
          <div className="text-overline flex items-center gap-2"><TrendingUp size={12} /> Last 10</div>
        </div>
        {hours.recent?.length === 0 ? (
          <div className="text-zinc-500 text-sm py-8 text-center">No punch records yet. Hit Punch In to start your first shift.</div>
        ) : (
          <div className="-mx-4 md:mx-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px] px-4 md:px-0">
              <thead>
                <tr className="text-overline text-left">
                  <th className="py-3 px-3">In</th>
                  <th className="py-3 px-3">Out</th>
                  <th className="py-3 px-3">Paid</th>
                  <th className="py-3 px-3">Break</th>
                  <th className="py-3 px-3">Address</th>
                </tr>
              </thead>
              <tbody>
                {hours.recent.map((r) => {
                  const ci = new Date(r.clock_in_at);
                  const co = r.clock_out_at ? new Date(r.clock_out_at) : null;
                  const paid = r.paid_seconds !== undefined ? r.paid_seconds : null;
                  const brkMin = Math.round((r.break_seconds || 0) / 60);
                  return (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="py-3 px-3 text-zinc-300 whitespace-nowrap">{ci.toLocaleString()}</td>
                      <td className="py-3 px-3 text-zinc-300 whitespace-nowrap">{co ? co.toLocaleString() : <span className="text-[#FF3B30]">active</span>}</td>
                      <td className="py-3 px-3 font-mono">{paid !== null ? fmt(paid) : "—"}</td>
                      <td className="py-3 px-3 font-mono text-amber-400">{brkMin > 0 ? `${brkMin}m` : "—"}</td>
                      <td className="py-3 px-3 text-zinc-500 max-w-xs truncate">{r.clock_in_address || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className={`brand-surface rounded-2xl p-4 md:p-6 hover-lift ${accent ? "border-[#FF3B30]/30" : ""}`} data-testid={`hours-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-overline text-[10px] md:text-[11px]">{label}</div>
      <div className={`font-mono font-bold text-2xl md:text-4xl mt-2 md:mt-3 ${accent ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
      {sub && <div className="text-[11px] text-amber-400 mt-1">{sub}</div>}
    </div>
  );
}
